import { useId } from 'react';
import { cn } from '@/lib/utils';
import { FLOW_COLORS, FLOW_DROP, FLOW_TIDE } from './flowGlyph';

export interface FlowMarkProps {
  /** Rendered size in px. The mark is drawn on a 512-unit grid and scales cleanly. */
  size?: number;
  className?: string;
  /** Accessible name. Pass `null` when the word "Flow" sits right beside it. */
  title?: string | null;
}

/**
 * The Flow brand mark: a cream drop on Ember's navy coin, half full with the tide in the flame's
 * three tones, so the two apps read as siblings — Ember is the flame, Flow is the drop. The
 * drawing comes from scripts/brand (see docs/DESIGN_SYSTEM.md, "Brand"); `public/favicon.svg`
 * and `public/brand/` are the same paths. It is paths, not text, so it never waits on a font.
 */
export function FlowMark({ size = 32, className, title = 'Flow' }: FlowMarkProps) {
  // The clip id must be unique per instance — the sidebar and the mobile top bar can both be in
  // the document — and url(#…) refs cannot contain the colons useId emits.
  const clip = `${useId().replace(/[^a-zA-Z0-9_-]/g, '')}-drop`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-label={title ?? undefined}
      aria-hidden={title ? undefined : true}
    >
      <circle cx="256" cy="256" r="256" fill={FLOW_COLORS.navy} />
      <path d={FLOW_DROP} fill={FLOW_COLORS.cream} />
      <clipPath id={clip}>
        <path d={FLOW_DROP} />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        {FLOW_TIDE.map((band) => (
          <path key={band.fill} d={band.d} fill={band.fill} />
        ))}
      </g>
    </svg>
  );
}
